import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDirtyTracker } from '../../../../context/UpdateContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useTheme } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormControlLabel, Modal } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { GridCloseIcon } from '@mui/x-data-grid';
import ProductPickerModal from '../../common/ProductPickerModal';

import {
  createTrial,
  createTrialTicket,
  getTrialById,
  updateTrial,
  updateTrialTicket,
  deleteTrialById,
  changeStatusTrial,
} from '../../../../api/ticket';
import { centersDetail } from '../../../../api/store-management';
import { errorMessages, imageValidation, validateDateRange, sellStatus } from '../../../../utils/methods';
import { appendField } from '../../../../utils/voucherFormData';
import { toggleTrialExposure } from '../../../../api/exposure';
import { FAVORITE_TARGET } from '../../../../api/favorite';
import FavoriteStar from '../../../common/FavoriteStar';
import useFavorite from '../../../../utils/useFavorite';
import { getDirectBranches } from '../../../../api/point';
import ConfirmModal from '../../../common/modal/ConfirmModal';

import IOSSwitch from '../../IosSwitch';
import useUserAuth from '../../../../utils/useUserAuth';
import BannerDeleteModal from '../../../banner/BannerDeleteModal';
import TrialEditStep01 from './TrialEditStep01';
import TrialEditStep02 from './TrialEditStep02';

import {
  PageContainer,
  Header,
  HeaderTitle,
  Divider,
  EditModeHeader,
  StatusToggleWrapper,
  StatusToggleLabel,
  ToggleContainer,
  ToggleLabel,
  StyledSwitch,
  TrialInfoWrapper,
  TrialInfoHeader,
  TrialInfoTitle,
  StatusBadge,
  CopyButton,
  LoadProductButton,
  HeaderRightGroup,
  CopyModeBanner,
  CopyModeBannerTitle,
  CopyModeBannerText,
  WishlistWrapper,
  WishlistIcon,
  WishlistText,
  ModalContainer,
  ModalCloseIcon,
  ModalIconBadge,
  ModalContent,
  ModalTitle,
  ModalDescription,
  ModalDescText,
  ModalDescTextSub,
  ModalButtonWrapper,
  ModalCancelButton,
  ModalConfirmButton,
} from './TrialEditStyled';

// 상수 정의
const CLASS_TYPES = [
  { label: '개인 레슨', value: 'PRIVATE_LESSON' },
  { label: '그룹 레슨', value: 'GROUP_LESSON' },
  { label: 'OT', value: 'OT' },
  { label: '자유 이용', value: 'PASS' },
];

const TrialEdit = () => {
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // 복사: /trial/create?copyFrom={id} — 편집 로더를 그대로 재사용한다
  const copyFromId = searchParams.get('copyFrom');
  const user = useSelector(state => state.user.user);

  const isEditMode = !!id;
  const isCopyMode = !isEditMode && !!copyFromId;
  // 지점 공용 즐겨찾기(상세 헤더 별표)
  const [favorite, setFavorite] = useFavorite(FAVORITE_TARGET.TRIAL, id, isEditMode);
  const [legacyBranchNames, setLegacyBranchNames] = useState({});

  // 같은 본사 산하 직영점 목록 (#73 다지점 공유 — 지점 계정도 본사 직영점 전체 수신)
  const { data: directBranches = [] } = useQuery({
    queryKey: ['directBranches', user?.branch?.branchId],
    queryFn: () => getDirectBranches(user.token, user.branch?.branchId),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 생성 모드 기본 판매·이용지점 = 로그인 지점 (#73)
  useEffect(() => {
    if (!isEditMode && user?.branch?.branchId) {
      if (!getValues('saleBranchIds')?.length) setValue('saleBranchIds', [user.branch.branchId]);
      if (getValues('useSaleBranchYn') !== 'Y' && !getValues('useBranchIds')?.length) setValue('useBranchIds', [user.branch.branchId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, user?.branch?.branchId]);
  const canCreate = useUserAuth('TICKET_TRIAL_CREATE');
  const canEdit = useUserAuth('TICKET_TRIAL_EDIT');
  const canSave = isEditMode ? canEdit : canCreate;

  // 지점별 sportTypes 조회 (centerId로 조회)
  const { data: centrerInfo } = useQuery({
    queryKey: ['centerInfo', user?.branch?.centerId],
    queryFn: () => centersDetail(user?.token, user?.branch?.centerId),
    enabled: !!user?.branch?.centerId,
  });

  // Step 상태
  const [currentStep, setCurrentStep] = useState(1);

  // 기본 상태
  const [loader, setLoader] = useState(false);
  const [trialData, setTrialData] = useState({
    voucherList: [],
    regularPrice: 0,
    discountRate: 0,
    finalPrice: 0,
    saleStatus: false,
  });

  const [exposureYn, setExposureYn] = useState('Y');

  const [exposureAlertOpen, setExposureAlertOpen] = useState(false);

  const handleExposureToggle = async () => {
    const newValue = exposureYn === 'Y' ? 'N' : 'Y';
    const saleStatus = watch('saleStatus');
    if (newValue === 'Y' && (saleStatus === 'EXPIRED' || saleStatus === 'SALE_END')) {
      setExposureAlertOpen(true);
      return;
    }
    setExposureYn(newValue);
    try {
      await toggleTrialExposure(user.token, id, newValue);
      toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨김 처리됩니다');
    } catch (error) {
      setExposureYn(exposureYn);
      toast.error(errorMessages(error));
    }
  };

  // Step01 상태
  const [additionalProductSettings, setAdditionalProductSettings] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeSettings, setTimeSettings] = useState({
    startHour: '00',
    startMinute: '00',
    endHour: '00',
    endMinute: '00',
  });
  const [usagePeriodType, setUsagePeriodType] = useState(true);
  const [isTaxFree, setIsTaxFree] = useState(false);
  const [discountType, setDiscountType] = useState('%');
  const [hasDiscountEndDate, setHasDiscountEndDate] = useState(true);
  const [lastChangedField, setLastChangedField] = useState(null);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  // 입장 가능 출입문 — 선택된 출입문(BranchDoor) id 목록. 미선택(빈 배열)이면 accessGroupIds 를 전송하지 않는다.
  const [selectedDoorIds, setSelectedDoorIds] = useState([]);
  // 출입문 체계 전환 지점 여부 — 전환 지점은 저장 시 항상 accessGroupIds 를 전송한다.
  const [doorBranchConverted, setDoorBranchConverted] = useState(false);

  // Step02 상태
  const [aiToggle, setAiToggle] = useState(false);
  const [curriculumList, setCurriculumList] = useState([{ title: '', description: '', images: [] }]);

  // Modal 상태
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceName, setCopySourceName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const defaultValues = useMemo(() => {
    const sportTypeFromData = trialData?.sportType;
    return {
      // sports는 multi-select 태그 UI에서 배열로 관리
      sports: sportTypeFromData ? (Array.isArray(sportTypeFromData) ? sportTypeFromData : [sportTypeFromData]) : [],
      // 회원앱 노출 (FE-only, 백엔드 미연동)
      appDisplayYn: 'Y',
      // 이용구분 기간제/횟수제 (FE-only)
      usageType: 'PERIOD',
      // 종료일 없음 기본값
      hasSaleEndDate: true,
      // 무제한 체크박스 기본값
      maxTrialPerUserUnlimited: true,
      totalQuantityUnlimited: true,
      // 중지권 기본값: 무제한
      stopType: 'UNLIMITED',
      cancelCounts: true,
      reservationCounts: true,
      dailyUsageLimits: true,
      weeklyUsageLimits: true,
    };
  }, [trialData]);

  const { handleSubmit, control, setValue, getValues, reset, watch, formState } = useForm({ defaultValues });
  useDirtyTracker(formState.isDirty);

  // ============================================
  // Data Fetching (Edit Mode)
  // ============================================

  const getTrial = async (sourceId, forCopy = false) => {
    try {
      const data = await getTrialById(sourceId, user.token);
      setTrialData({
        ...data,
        voucherList: data.images?.map(item => ({ voucherUrl: item })) || [],
      });

      // 커리큘럼 데이터 설정
      if (data.curriculums && data.curriculums.length > 0) {
        setCurriculumList(
          data.curriculums.map(item => ({
            title: item.title || '',
            description: item.summary || '',
            images: item.images?.length > 0 ? item.images.map(url => ({ url, preview: url })) : [],
          })),
        );
      }

      reset(data);
      // #73 판매·이용지점 프리필 — 비공유 상품은 소유 지점 1개
      {
        const fallbackIds = data?.participatingBranchIds?.length
          ? data.participatingBranchIds
          : [data?.branchId ?? user?.branch?.branchId].filter(Boolean);
        setValue('saleBranchIds', data?.saleBranchIds?.length ? data.saleBranchIds : fallbackIds);
        // #98 소속지점 모드면 이용지점 목록은 비어 있는 게 정상
        setValue('useSaleBranchYn', data?.useSaleBranchYn === 'Y' ? 'Y' : 'N');
        setValue('useBranchIds', data?.useSaleBranchYn === 'Y' ? [] : (data?.useBranchIds?.length ? data.useBranchIds : fallbackIds));
      }
      // #73 판매/이용 옵션(직영점 목록)에 없는 참여지점(과거 가맹점 데이터 등) 이름 폴백용
      setLegacyBranchNames(
        (data?.participatingBranchIds || []).reduce((acc, bid, idx) => {
          acc[bid] = data.participatingBranchNames?.[idx] ?? `지점 #${bid}`;
          return acc;
        }, {}),
      );
      if (data?.exposureYn) setExposureYn(data.exposureYn);
      setSelectedDoorIds((data?.accessGroups ?? []).map(g => g.id));
      setAdditionalProductSettings(
        data?.products?.map(el => ({
          name: el.name,
          price: el.price,
          use: el.status === 'USED',
        })) || [],
      );

      // sports는 배열로 세팅 (백엔드 sportTypes 배열 우선, 없으면 단일 sportType)
      const rawLoaded = data?.sportTypes ?? data?.sportType;
      const loadedSports = rawLoaded ? (Array.isArray(rawLoaded) ? rawLoaded : [rawLoaded]) : [];
      setValue('sports', loadedSports);

      data?.totalQuantity ? setValue('totalQuantity', data?.totalQuantity) : setValue('totalQuantityUnlimited', true);
      data?.maxTrialPerUser ? setValue('maxTrialPerUser', data?.maxTrialPerUser) : setValue('maxTrialPerUserUnlimited', true);
      data?.dailyUsageLimit ? setValue('dailyUsageLimit', data?.dailyUsageLimit) : setValue('dailyUsageLimits', true);
      data?.weeklyUsageLimit
        ? setValue('weeklyUsageLimit', data?.weeklyUsageLimit)
        : setValue('weeklyUsageLimits', true);
      // 중지권 매핑: null=무제한, 0=중지불가, 양수=직접입력
      if (data?.stopCount !== null && data?.stopCount !== undefined && data?.stopCount !== '') {
        setValue('stopCount', data?.stopCount);
        setValue('stopPeriod', data?.stopPeriod);
        setValue('stopType', Number(data?.stopCount) > 0 ? 'CUSTOM' : 'DISABLED');
      } else {
        setValue('stopType', 'UNLIMITED');
      }
      if (data?.cancelCount != null && data?.cancelCount !== '') {
        setValue('cancelCount', data.cancelCount);
        setValue('cancelCounts', false);
      } else {
        setValue('cancelCounts', true);
      }
      data?.reservationCount
        ? setValue('reservationCount', data?.reservationCount)
        : setValue('reservationCounts', true);

      // 할인 타입 설정 (RATE → %, AMOUNT → 원)
      if (data?.discountType) {
        setDiscountType(data.discountType === 'AMOUNT' ? '원' : '%');
      }

      setUsagePeriodType(data?.usagePeriodType === 'MONTH');
      setValue('hasSaleEndDate', !data?.hasSaleEndDate);
      // 할인 종료일 유무: BE true(종료일 있음) ↔ FE state false(종료일 없음 체크박스 안됨)
      setHasDiscountEndDate(!data?.hasDiscountEndDate);

      // 이용구분 (기간제/횟수제): BE 의 usageType 우선, 없으면 usageCount 로 추론(레거시 폴백).
      if (data?.classType === 'PASS') {
        setValue('usageType', data?.usageType || (Number(data?.usageCount) > 1 ? 'COUNT' : 'PERIOD'));
      }

      // 요일 설정 파싱 (예: "MON,WED" -> ['MON', 'WED'])
      if (data?.allowedDays) {
        const days = data.allowedDays.split(',').filter(day => day.trim());
        setSelectedDays(days);
      }

      // 시간 설정 파싱 (예: "09:00:00" -> startHour: '09', startMinute: '00')
      if (data?.startTime) {
        const [startHour, startMinute] = data.startTime.split(':');
        setTimeSettings(prev => ({
          ...prev,
          startHour: startHour || '00',
          startMinute: startMinute || '00',
        }));
      }
      if (data?.endTime) {
        const [endHour, endMinute] = data.endTime.split(':');
        setTimeSettings(prev => ({
          ...prev,
          endHour: endHour || '00',
          endMinute: endMinute || '00',
        }));
      }

      // 복사본은 이름만 비우고(원본명은 placeholder 로 안내) 미노출 상태로 시작한다
      if (forCopy) {
        setCopySourceName(data?.trialName ?? '');
        setValue('trialName', '');
        setExposureYn('N');
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 편집(:id) / 복사(?copyFrom) 모두 같은 로더를 쓴다
  useEffect(() => {
    const sourceId = id ?? copyFromId;
    if (sourceId) {
      getTrial(sourceId, isCopyMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, copyFromId]);

  // ============================================
  // Product Handlers
  // ============================================

  const handleAddProduct = product => {
    setAdditionalProductSettings(prev => [
      ...prev,
      { name: product.productName, price: product.price, productId: product.productId },
    ]);
  };

  const handleRemoveProduct = index => {
    setAdditionalProductSettings(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================
  // Image Handlers
  // ============================================

  const uploadImages = async e => {
    const files = e.target.files;
    const currentLength = [...trialData.voucherList, ...files].length;
    const imageLimit = 7;

    if (currentLength > imageLimit) {
      alert(`You can only upload up to ${imageLimit} images.`);
      return;
    }

    const images = [];
    for (const file of files) {
      try {
        const loadedImage = await imageValidation(file);
        images.push({ voucherImg: loadedImage });
      } catch (error) {
        toast.error(errorMessages(error));
      }
    }

    setTrialData({
      ...trialData,
      voucherList: [...trialData.voucherList, ...images],
    });
  };

  const deleteVoucher = index => {
    setTrialData({
      ...trialData,
      voucherList: trialData.voucherList.filter((_, i) => i !== index),
    });
  };

  // ============================================
  // Curriculum Handlers
  // ============================================

  const handleCurriculumChange = (index, field, value) => {
    setCurriculumList(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleCurriculumImageUpload = async (curriculumIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const loadedImage = await imageValidation(file);
      setCurriculumList(prev =>
        prev.map((item, i) => {
          if (i === curriculumIndex) {
            const images = item.images || [];
            if (images.length >= 3) {
              toast.error('이미지는 최대 3장까지 업로드 가능합니다.');
              return item;
            }
            return { ...item, images: [...images, { file: loadedImage }] };
          }
          return item;
        }),
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleCurriculumImageDelete = (curriculumIndex, imageIndex) => {
    setCurriculumList(prev =>
      prev.map((item, i) => {
        if (i === curriculumIndex) {
          return { ...item, images: item.images.filter((_, imgI) => imgI !== imageIndex) };
        }
        return item;
      }),
    );
  };

  const addCurriculum = () => {
    if (curriculumList.length >= 5) {
      toast.error('커리큘럼은 최대 5개까지 등록 가능합니다.');
      return;
    }
    setCurriculumList(prev => [...prev, { title: '', description: '', images: [] }]);
  };

  const deleteCurriculum = index => {
    setCurriculumList(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================
  // Price Calculation
  // ============================================

  useEffect(() => {
    if (!lastChangedField) return;

    const regularPrice = watch('regularPrice');
    const discount = watch('discountRate');
    const finalPrice = watch('finalPrice');

    const regularPriceNum = Number(regularPrice?.toString().replace(/,/g, '') || 0);
    const discountRateNum = Number(discount?.toString().replace(/,/g, '') || 0);
    const finalPriceNum = Number(finalPrice?.toString().replace(/,/g, '') || 0);

    if (lastChangedField === 'regularPrice' && regularPriceNum > 0) {
      setValue('finalPrice', regularPriceNum);
      setValue('discountRate', 0);
    } else if (lastChangedField === 'discountRate' && regularPriceNum > 0) {
      if (discountRateNum < 0) {
        toast.error(discountType === '원' ? '할인금액은 음수가 될 수 없습니다' : '할인율은 음수가 될 수 없습니다');
        setLastChangedField(null);
        return;
      }
      if (discountType === '%') {
        if (discountRateNum > 100) {
          toast.error('할인율은 100%를 초과할 수 없습니다');
          setLastChangedField(null);
          return;
        }
        const calculatedFinalPrice = Math.round(regularPriceNum - (regularPriceNum * discountRateNum) / 100);
        setValue('finalPrice', calculatedFinalPrice);
      } else {
        if (discountRateNum > regularPriceNum) {
          toast.error('할인금액이 정상가보다 클 수 없습니다');
          setLastChangedField(null);
          return;
        }
        setValue('finalPrice', regularPriceNum - discountRateNum);
      }
    } else if (lastChangedField === 'finalPrice' && regularPriceNum > 0) {
      if (finalPriceNum > regularPriceNum) {
        toast.error('최종가는 정상가보다 클 수 없습니다');
        setLastChangedField(null);
        return;
      }
      if (finalPriceNum < 0) {
        toast.error('최종가는 음수가 될 수 없습니다');
        setLastChangedField(null);
        return;
      }
      if (discountType === '%') {
        let calculatedDiscountRate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
        if (finalPriceNum > 0 && calculatedDiscountRate >= 100) {
          calculatedDiscountRate = 99;
        }
        setValue('discountRate', calculatedDiscountRate);
      } else {
        setValue('discountRate', regularPriceNum - finalPriceNum);
      }
    }

    setLastChangedField(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastChangedField]);

  // 단위 전환 시 기존 값 변환 (% ↔ 원)
  useEffect(() => {
    const regularPriceNum = Number(getValues('regularPrice')?.toString().replace(/,/g, '') || 0);
    const finalPriceNum = Number(getValues('finalPrice')?.toString().replace(/,/g, '') || 0);
    if (regularPriceNum <= 0) return;

    if (discountType === '원') {
      setValue('discountRate', Math.max(0, regularPriceNum - finalPriceNum));
    } else {
      const rate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
      setValue('discountRate', Math.max(0, Math.min(rate, 99)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountType]);

  const handleRegularPriceChange = () => setLastChangedField('regularPrice');
  const handleDiscountRateChange = () => setLastChangedField('discountRate');
  const handleFinalPriceChange = () => setLastChangedField('finalPrice');

  // ============================================
  // Step Navigation
  // ============================================

  const handleNext = () => {
    setCurrentStep(2);
  };

  const handlePrev = () => {
    setCurrentStep(1);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // ============================================
  // Submit Handler
  // ============================================

  const submit = async values => {
    setLoader(true);

    // 유효성 검사
    const hasSaleEndDate = values.hasSaleEndDate ?? false;
    if (!hasSaleEndDate && !validateDateRange(values.saleStartDate, values.saleEndDate)) {
      toast.error('기간을 다시 확인해주세요');
      setLoader(false);
      return;
    }

    // 가격 파싱 함수
    const parsePrice = value => {
      if (value === null || value === undefined) return 0;
      return Number(value.toString().replace(/,/g, ''));
    };

    // FormData 생성
    const formData = new FormData();

    // 기본 필드
    formData.append('branchId', user?.branch?.branchId || 0);
    // #73 판매지점·이용지점 — 합집합 2개 이상이면 공유 상품으로 저장된다
    const saleToSend = (getValues('saleBranchIds') || []).filter(Boolean);
    const useToSend = (getValues('useBranchIds') || []).filter(Boolean);
    // #98 소속지점 모드 — 이용지점 목록 대신 플래그만 보낸다(백엔드가 이용 행을 비운다)
    const useSaleBranch = getValues('useSaleBranchYn') === 'Y';
    formData.append('useSaleBranchYn', useSaleBranch ? 'Y' : 'N');
    (saleToSend.length ? saleToSend : [user?.branch?.branchId]).forEach(bid => {
      formData.append('saleBranchIds', bid);
    });
    if (!useSaleBranch) {
      (useToSend.length ? useToSend : [user?.branch?.branchId]).forEach(bid => {
        formData.append('useBranchIds', bid);
      });
    }
    formData.append('classType', values.classType || 'PRIVATE_LESSON');

    // 운동종목: 다중 선택 배열 → 백엔드로 전송 (수강권/패스와 동일 패턴, BE 다중 지원 대비)
    const sportsArr = Array.isArray(values.sports) ? values.sports : values.sports ? [values.sports] : [];
    if (sportsArr.length > 0) {
      formData.append('sportType', sportsArr[0]); // 대표값(단일)
      appendField(formData, 'sportTypes', sportsArr); // 복수 종목(키 반복)
    }

    formData.append('trialName', values.trialName);
    formData.append('regularPrice', parsePrice(values.regularPrice));
    formData.append('discountRate', parsePrice(values.discountRate));
    formData.append('discountType', discountType === '%' ? 'RATE' : 'AMOUNT');
    formData.append('finalPrice', parsePrice(values.finalPrice));
    formData.append('saleStartDate', dayjs(values.saleStartDate).format('YYYY-MM-DD'));
    if (!hasSaleEndDate && values.saleEndDate) {
      formData.append('saleEndDate', dayjs(values.saleEndDate).format('YYYY-MM-DD'));
    }
    appendField(formData, 'hasSaleEndDate', hasSaleEndDate); // 헬퍼가 FE↔BE 의미 반전 처리
    // 이용권(PASS)형 일일권: 이용기간 1일 고정 + 기간제. 이용설정(중지권/이용제한/요일/시간대)은 미전송(=무제한/미설정).
    const isPassType = values.classType === 'PASS';
    formData.append('usagePeriodType', isPassType ? 'DAY' : usagePeriodType ? 'MONTH' : 'DAY');
    formData.append('usagePeriod', isPassType ? 1 : Number(values.usagePeriod) || 0);
    formData.append('usageCount', isPassType ? 1 : Number(values.usageCount) || 1);
    // 이용구분: 이용권(PASS)은 기간제 고정, 레슨은 항상 횟수제(COUNT).
    formData.append('usageType', isPassType ? 'PERIOD' : 'COUNT');
    formData.append('exposureYn', exposureYn);
    if (!values.maxTrialPerUserUnlimited) formData.append('maxTrialPerUser', Number(values.maxTrialPerUser) || 0);
    if (!isPassType && !getValues('cancelCounts')) formData.append('cancelCount', Number(values.cancelCount) || 0);
    if (!isPassType && !getValues('reservationCounts')) {
      formData.append('reservationCount', Number(values.reservationCount) || 0);
    }
    formData.append('classDefaultMinute', Number(values.classDefaultMinute) || 60);
    if (!values.totalQuantityUnlimited) formData.append('totalQuantity', Number(values.totalQuantity) || 0);
    formData.append('classDetail', values.classDetail || '');
    formData.append('post', true);

    // 중지권: 무제한=미전송(null), 중지불가=0, 직접입력=양수. PASS 는 항상 미전송(무제한).
    const stopType = getValues('stopType');
    if (!isPassType && stopType === 'DISABLED') {
      formData.append('stopCount', 0);
      formData.append('stopPeriod', 0);
    } else if (!isPassType && stopType === 'CUSTOM') {
      formData.append('stopCount', Number(values.stopCount));
      formData.append('stopPeriod', Number(values.stopPeriod) || 0);
    }
    // UNLIMITED: 미전송 → BE 가 null 로 저장 (= 무제한)

    // 이용제한: dailyUsageLimit / weeklyUsageLimit 각각 독립으로 무제한 시 미전송. PASS 는 항상 미전송.
    if (!isPassType && !getValues('dailyUsageLimits') && values.dailyUsageLimit) {
      formData.append('dailyUsageLimit', Number(values.dailyUsageLimit));
    }
    if (!isPassType && !getValues('weeklyUsageLimits') && values.weeklyUsageLimit) {
      formData.append('weeklyUsageLimit', Number(values.weeklyUsageLimit));
    }

    // 할인기간 (hasDiscountEndDate FE 의미 = "종료일 없음 체크" → BE 로는 반전해서 전송)
    if (values.discountStartDate) {
      formData.append('discountStartDate', dayjs(values.discountStartDate).format('YYYY-MM-DD'));
    }
    if (!hasDiscountEndDate && values.discountEndDate) {
      formData.append('discountEndDate', dayjs(values.discountEndDate).format('YYYY-MM-DD'));
    }
    appendField(formData, 'hasDiscountEndDate', hasDiscountEndDate); // 헬퍼가 FE↔BE 의미 반전 처리

    // allowedDays / 시간 설정: PASS 는 미전송 → BE 가 요일·시간 제한을 제거한다
    if (!isPassType) {
      if (selectedDays.length > 0) {
        formData.append('allowedDays', selectedDays.join(','));
      }
      const startTimeStr = `${timeSettings.startHour}:${timeSettings.startMinute}:00`;
      const endTimeStr = `${timeSettings.endHour}:${timeSettings.endMinute}:00`;
      formData.append('startTime', startTimeStr);
      formData.append('endTime', endTimeStr);
    }

    // trainers 배열
    selectedTrainers.forEach(trainerId => {
      formData.append('trainers', trainerId);
    });

    // 입장 가능 출입문 — 전환 지점은 항상 반영(빈 선택=전체 해제 플래그), 미전환은 기존대로.
    if (selectedDoorIds.length > 0) {
      selectedDoorIds.forEach(doorId => formData.append('accessGroupIds', doorId));
    } else if (doorBranchConverted) {
      formData.append('clearAccessGroups', 'true');
    }

    // images 처리
    trialData.voucherList?.forEach(voucher => {
      if (voucher.voucherImg instanceof File) {
        formData.append('images', voucher.voucherImg);
      } else if (voucher.voucherUrl) {
        formData.append('imageStrings', voucher.voucherUrl);
      }
    });

    // 커리큘럼 처리
    curriculumList.forEach((cur, index) => {
      formData.append(`curriculums[${index}].title`, cur.title || '');
      formData.append(`curriculums[${index}].summary`, cur.description || '');
      if (cur.images && cur.images.length > 0) {
        cur.images.forEach(img => {
          if (img.file) {
            formData.append(`curriculums[${index}].images`, img.file);
          } else if (img.url) {
            formData.append(`curriculums[${index}].imageStrings`, img.url);
          }
        });
      }
    });

    // products 배열
    additionalProductSettings.forEach((product, index) => {
      formData.append(`products[${index}].name`, product.name);
      formData.append(`products[${index}].price`, Number(product.price) || 0);
      formData.append(`products[${index}].branchProductId`, product.productId || 0);
      formData.append(`products[${index}].status`, product.use ? 'USED' : 'UNUSED');
    });

    try {
      if (isEditMode) {
        const updateRes = await updateTrialTicket(id, formData, user.token);
        toast.success(updateRes?.message || t('성공적으로 수정되었습니다'));
      } else {
        const createRes = await createTrialTicket(formData, user.token);
        toast.success(createRes?.message || t('성공적으로 생성되었습니다'));
      }
      queryClient.invalidateQueries({ queryKey: ['trials'] });
      navigate('/trial');
    } catch (error) {
      toast.error(errorMessages(error));
      setLoader(false);
    }
  };

  // ============================================
  // Status Change Handler (Edit Mode)
  // ============================================

  const changeTrialStatus = async e => {
    const status = e.target.checked ? 'APPROVED' : 'EXPIRED';
    try {
      const res = await changeStatusTrial(status, id, user.token);
      toast.success(res?.message || t('상태가 변경되었습니다'));
      setValue('saleStatus', status);
      if (status === 'EXPIRED') setExposureYn('N');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // ============================================
  // Copy Handler (Edit Mode)
  // ============================================

  const copyTrialData = () => {
    navigate(`/trial/create?copyFrom=${id}`);
  };

  // ============================================
  // Delete Handler (Edit Mode)
  // ============================================

  const deleteTrial = async () => {
    setLoader(true);
    try {
      const res = await deleteTrialById(id, user.token);
      toast.success(res?.message || t('삭제되었습니다'));
      navigate('/trial');
    } catch (error) {
      toast.error(errorMessages(error));
      setLoader(false);
    }
  };

  // ============================================
  // Render
  // ============================================

  return (
    <>
    <PageContainer>
      {/* Header */}
      <Header>
        <Link style={{ textDecoration: 'none', color: '#3a3a3a', display: 'flex', alignItems: 'center' }} to="/trial">
          <svg id="뒤로가기" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path id="Path_6534" data-name="Path 6534" d="M0,0H24V24H0Z" fill="none" />
            <circle
              id="Ellipse_96"
              data-name="Ellipse 96"
              cx="11"
              cy="11"
              r="11"
              transform="translate(1 1)"
              fill="#3a3a3a"
            />
            <path
              id="Path_6535"
              data-name="Path 6535"
              d="M6.393,8.579H14V9.865H6.393l3.352,3.448-.884.909L4,9.222l4.861-5,.884.909Z"
              transform="translate(3 2.778)"
              fill="#fff"
            />
          </svg>
          <HeaderTitle>{isEditMode ? t('일일권 수정') : t('일일권 만들기')}</HeaderTitle>
        </Link>
        <HeaderRightGroup>
          {!isEditMode && (
            <LoadProductButton onClick={() => setPickerOpen(true)} variant="contained" disableElevation>
              <ContentCopyOutlinedIcon sx={{ width: '16px', height: '16px' }} />
              기존 상품 불러오기
            </LoadProductButton>
          )}
          <Link
            style={{ fontSize: '14px', fontWeight: 550, color: theme.palette.primary.main, textDecoration: 'none' }}
            to="/trial">
            {t('목록으로')}
          </Link>
        </HeaderRightGroup>
      </Header>

      <ProductPickerModal
        open={pickerOpen}
        kind="TRIAL"
        onClose={() => setPickerOpen(false)}
        onSelect={selectedId => {
          setPickerOpen(false);
          navigate(`/trial/create?copyFrom=${selectedId}`);
        }}
      />

      {isCopyMode && (
        <CopyModeBanner>
          <ContentCopyOutlinedIcon sx={{ width: '18px', height: '18px', color: '#3E9FA7' }} />
          <CopyModeBannerTitle>복사본 만드는 중</CopyModeBannerTitle>
          <CopyModeBannerText>
            {copySourceName ? `'${copySourceName}'` : '원본'} 의 설정을 불러왔습니다. 저장해야 새 일일권으로 생성됩니다.
          </CopyModeBannerText>
        </CopyModeBanner>
      )}

      {/* Edit Mode Header */}
      {isEditMode && (
        <>
          <Divider />
          <EditModeHeader>
            <ToggleContainer>
              <ToggleLabel>판매 {watch('saleStatus') === 'APPROVED' ? 'ON' : 'OFF'}</ToggleLabel>
              <StyledSwitch
                checked={watch('saleStatus') === 'APPROVED'}
                onChange={changeTrialStatus}
                disabled={user.userRole === 'ROLE_MANAGER' && trialData.saleStatus === 'APPROVAL_PENDING'}
              />
            </ToggleContainer>

            <TrialInfoWrapper>
              <TrialInfoHeader>
                <TrialInfoTitle>
                  {watch('trialName')}{' '}
                  {(() => {
                    const s = watch('sports');
                    if (!s) return '';
                    return Array.isArray(s) ? s.map(v => t(v)).join(', ') : t(s);
                  })()}{' '}
                  {watch('classType') === 'PASS'
                    ? '1일'
                    : `${watch('usagePeriod')}${usagePeriodType ? '개월' : '일'}`}
                </TrialInfoTitle>
                <StatusBadge variant="outlined">{sellStatus(watch('saleStatus'), t)}</StatusBadge>
                <FavoriteStar targetType={FAVORITE_TARGET.TRIAL} targetId={Number(id)} favorite={favorite} onChanged={setFavorite} />
              </TrialInfoHeader>
              <WishlistWrapper>
                <WishlistIcon src="/images/heart.svg" alt="love" />
                <WishlistText>찜하기 수 {trialData.wishListAddedCount ?? 0}</WishlistText>
              </WishlistWrapper>
            </TrialInfoWrapper>
            <CopyButton onClick={() => setCopyModalOpen(true)} size="large">
              일일권 복사
              <ContentCopyOutlinedIcon sx={{ width: '16px', height: '16px' }} />
            </CopyButton>

            {/* Copy Modal */}
            <Modal open={copyModalOpen} onClose={() => setCopyModalOpen(false)}>
              <ModalContainer>
                <ModalCloseIcon as={GridCloseIcon} onClick={() => setCopyModalOpen(false)} />
                <ModalIconBadge>
                  <ContentCopyOutlinedIcon sx={{ width: '22px', height: '22px', color: '#4AB3BC' }} />
                </ModalIconBadge>
                <ModalContent>
                  <ModalTitle>이 일일권을 복사해 새 상품을 만들까요?</ModalTitle>
                </ModalContent>
                <ModalDescription>
                  <ModalDescText>원본 설정이 채워진 일일권 등록 화면으로 이동합니다.</ModalDescText>
                  <ModalDescTextSub>일일권명을 입력하고 저장해야 새 상품이 생성됩니다.</ModalDescTextSub>
                </ModalDescription>
                <ModalButtonWrapper>
                  <ModalCancelButton onClick={() => setCopyModalOpen(false)} variant="contained">
                    {t('Cancel')}
                  </ModalCancelButton>
                  <ModalConfirmButton onClick={copyTrialData} variant="contained">
                    복사해서 만들기
                  </ModalConfirmButton>
                </ModalButtonWrapper>
              </ModalContainer>
            </Modal>
          </EditModeHeader>
          <Divider />
        </>
      )}

      {/* Step Content */}
      {currentStep === 1 ? (
        <TrialEditStep01
          branchOptions={directBranches || []}
          legacyBranchNames={legacyBranchNames}
          control={control}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          user={user}
          data={null}
          additionalProductSettings={additionalProductSettings}
          handleAddProduct={handleAddProduct}
          handleRemoveProduct={handleRemoveProduct}
          usagePeriodType={usagePeriodType}
          setUsagePeriodType={setUsagePeriodType}
          isTaxFree={isTaxFree}
          setIsTaxFree={setIsTaxFree}
          discountType={discountType}
          setDiscountType={setDiscountType}
          hasDiscountEndDate={hasDiscountEndDate}
          setHasDiscountEndDate={setHasDiscountEndDate}
          handleRegularPriceChange={handleRegularPriceChange}
          handleDiscountRateChange={handleDiscountRateChange}
          handleFinalPriceChange={handleFinalPriceChange}
          onNext={handleNext}
          onCancel={handleCancel}
          loader={loader}
          centrerInfo={centrerInfo}
          CLASS_TYPES={CLASS_TYPES}
          selectedTrainers={selectedTrainers}
          handleTrainerToggle={trainerId => {
            setSelectedTrainers(prev =>
              prev.includes(trainerId) ? prev.filter(id => id !== trainerId) : [...prev, trainerId],
            );
          }}
          trainerOptions={centrerInfo?.branchDetailInfo?.trainers || []}
          exposureYn={exposureYn}
          onExposureToggle={handleExposureToggle}
          isEditMode={isEditMode}
          selectedDoorIds={selectedDoorIds}
          onDoorIdsChange={setSelectedDoorIds}
          onDoorConvertedChange={setDoorBranchConverted}
          copySourceName={copySourceName}
        />
      ) : (
        <TrialEditStep02
          control={control}
          watch={watch}
          setValue={setValue}
          trialData={trialData}
          setTrialData={setTrialData}
          curriculumList={curriculumList}
          setCurriculumList={setCurriculumList}
          aiToggle={aiToggle}
          setAiToggle={setAiToggle}
          uploadImages={uploadImages}
          deleteVoucher={deleteVoucher}
          handleCurriculumChange={handleCurriculumChange}
          handleCurriculumImageUpload={handleCurriculumImageUpload}
          handleCurriculumImageDelete={handleCurriculumImageDelete}
          addCurriculum={addCurriculum}
          deleteCurriculum={deleteCurriculum}
          onPrev={handlePrev}
          onSubmit={handleSubmit(submit)}
          onCancel={handleCancel}
          loader={loader}
          isEditMode={isEditMode}
          canSave={canSave}
          exposureYn={exposureYn}
          setExposureYn={setExposureYn}
        />
      )}

      {/* Delete Modal (Edit Mode) */}
      {isEditMode && (
        <BannerDeleteModal open={deleteModalOpen} setOpen={setDeleteModalOpen} handleDelete={deleteTrial} />
      )}
    </PageContainer>

    <ConfirmModal
      open={exposureAlertOpen}
      onClose={() => setExposureAlertOpen(false)}
      onConfirm={() => setExposureAlertOpen(false)}
      message={`판매종료된 상품은\n앱 노출이 불가합니다.`}
      highlightText="__none__"
      highlightColor="#4AB3BC"
      cancelText="닫기"
      confirmText="확인"
      confirmColor="#4AB3BC"
    />
    </>
  );
};

export default TrialEdit;
